import React, { useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SecurityBoundary, PermissionBoundary } from '@/lib/security';
import { logDataAccess, logAdminAction } from '@/lib/security/audit';
import { SystemRole } from '@/types';
import { SetupTab } from '../components/SetupTab';
import { UserApprovalsTab } from '../components/UserApprovalsTab';
import { WorkflowManagementWrapper } from '../components/WorkflowManagementWrapper';
import { DataQualityTab } from '../components/DataQualityTab';
import { ConfigTab } from '../components/ConfigTab';
import { UnifiedZoneManagementTab } from '../components/UnifiedZoneManagementTab';
import { Shield, Users, Play, BarChart3, Settings, MapPin } from 'lucide-react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';

const ControlCenterPage: React.FC = () => {
  const { user } = useAuth();

  // Get initial tab from URL hash or default to 'setup'
  const getInitialTab = () => {
    const hash = window.location.hash.slice(1); // Remove the #
    const validTabs = ['setup', 'zones', 'approvals', 'workflows', 'quality', 'config'];
    return validTabs.includes(hash) ? hash : 'setup';
  };

  const [activeTab, setActiveTab] = React.useState(getInitialTab);

  const tabTriggerClass =
    'h-full rounded-none border-b-2 border-transparent ' +
    'data-[state=active]:border-primary data-[state=active]:bg-transparent ' +
    'px-4 pb-3 pt-2 font-medium text-muted-foreground ' +
    'data-[state=active]:text-foreground shadow-none transition-none';

  // Update URL hash when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    window.location.hash = value;

    // Log admin action for tab navigation
    if (user) {
      logAdminAction('tab_navigation', {
        resource: 'control_center',
        tab: value,
        previousTab: activeTab
      }).catch(err => console.warn('Failed to log tab navigation:', err));
    }
  };

  // Listen for hash changes (e.g., browser back/forward)
  React.useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      const validTabs = ['setup', 'zones', 'approvals', 'workflows', 'quality', 'config'];
      if (validTabs.includes(hash)) {
        setActiveTab(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Log data access when page loads
  useEffect(() => {
    if (user) {
      logDataAccess({
        resource: 'control_center',
        action: 'view',
        metadata: {
          activeTab,
          page: 'control-center'
        }
      }).catch(err => console.warn('Failed to log data access:', err));
    }
  }, [user, activeTab]);

  return (
    <SecurityBoundary
      requiredSystemRole={SystemRole.SUPER_ADMIN}
      auditPageAccess='control-center'
      tenantIsolation={false}
    >
      <div className='flex h-full w-full flex-col space-y-6 overflow-hidden p-6'>
        {/* Header */}
        <div className='flex items-start gap-3'>
          <Shield className='h-6 w-6 text-primary mt-1' />
          <div>
            <h1 className='text-3xl font-bold tracking-tight'>Control Center</h1>
            <p className='text-muted-foreground text-sm'>
              Super admin controls for user access, system workflows, and execution monitoring
            </p>
          </div>
        </div>

        {/* Main Content */}
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className='flex-1 min-h-0 flex flex-col'
        >
          <div className='border-b'>
            <TabsList className='bg-transparent h-12 p-0 space-x-6'>
              <TabsTrigger value='setup' className={tabTriggerClass}>
                <Settings className='h-4 w-4 mr-2' />
                Tenant Setup
              </TabsTrigger>
              <TabsTrigger value='zones' className={tabTriggerClass}>
                <MapPin className='h-4 w-4 mr-2' />
                Zone Setup
              </TabsTrigger>
              <TabsTrigger value='approvals' className={tabTriggerClass}>
                <Users className='h-4 w-4 mr-2' />
                User Setup
              </TabsTrigger>
              <TabsTrigger value='workflows' className={tabTriggerClass}>
                <Play className='h-4 w-4 mr-2' />
                Workflow Management
              </TabsTrigger>
              <TabsTrigger value='quality' className={tabTriggerClass}>
                <BarChart3 className='h-4 w-4 mr-2' />
                Data Quality
              </TabsTrigger>
              <TabsTrigger value='config' className={tabTriggerClass}>
                <Settings className='h-4 w-4 mr-2' />
                Config
              </TabsTrigger>
            </TabsList>
          </div>

          <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
            <TabsContent value='setup' className='m-0 h-full overflow-y-auto py-6'>
              <PermissionBoundary require={{ resource: 'users', action: 'manage' }}>
                <SetupTab />
              </PermissionBoundary>
            </TabsContent>
            <TabsContent value='zones' className='m-0 flex min-h-0 flex-1 flex-col overflow-hidden'>
              <PermissionBoundary require={{ resource: 'users', action: 'manage' }}>
                <UnifiedZoneManagementTab />
              </PermissionBoundary>
            </TabsContent>
            <TabsContent value='approvals' className='m-0 h-full overflow-y-auto py-6'>
              <PermissionBoundary require={{ resource: 'users', action: 'manage' }}>
                <UserApprovalsTab />
              </PermissionBoundary>
            </TabsContent>
            <TabsContent value='workflows' className='m-0 h-full overflow-y-auto py-6'>
              <PermissionBoundary require={{ resource: 'users', action: 'manage' }}>
                <WorkflowManagementWrapper />
              </PermissionBoundary>
            </TabsContent>
            <TabsContent value='quality' className='m-0 h-full overflow-y-auto py-6'>
              <PermissionBoundary require={{ resource: 'users', action: 'manage' }}>
                <DataQualityTab />
              </PermissionBoundary>
            </TabsContent>
            <TabsContent value='config' className='m-0 h-full overflow-y-auto py-6'>
              <PermissionBoundary require={{ resource: 'users', action: 'manage' }}>
                <ConfigTab />
              </PermissionBoundary>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </SecurityBoundary>
  );
};

export default ControlCenterPage;
